import { FORMAT_CAPABILITIES, type SupportLevel } from '../../lib/formats/capabilities';
import { Badge, Card, SectionHeading, type Tone } from '../ui';

/**
 * What CleanFile does with each format, read straight from the capability table the parsers and the
 * error messages use. A format cannot appear here as cleanable unless the code treats it that way.
 */

const LEVEL_LABEL: Record<SupportLevel, string> = {
  full: 'Full clean',
  scan: 'Scan only',
  unsupported: 'Not supported',
};

const LEVEL_TONE: Record<SupportLevel, Tone> = {
  full: 'safe',
  scan: 'found',
  unsupported: 'neutral',
};

export function FormatSupport() {
  return (
    <section aria-labelledby="formats-heading" className="mt-14 text-left">
      <SectionHeading
        id="formats-heading"
        title="What CleanFile can do, by format"
        description="A format is only listed as a full clean if the metadata is removed and the result checked by reading the file back. The rest are named and refused rather than handed back unchanged."
      />

      <Card className="overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full min-w-[34rem] text-left text-sm">
            <thead className="text-muted border-b border-[var(--border)] text-xs tracking-wide uppercase">
              <tr>
                <th scope="col" className="px-4 py-2.5 font-medium">
                  Format
                </th>
                <th scope="col" className="px-4 py-2.5 font-medium">
                  Support
                </th>
                <th scope="col" className="px-4 py-2.5 font-medium">
                  What happens
                </th>
              </tr>
            </thead>
            <tbody>
              {FORMAT_CAPABILITIES.map((entry) => (
                <tr key={entry.label} className="border-b border-[var(--border)] align-top last:border-0">
                  <td className="px-4 py-3">
                    <span className="text-strong font-medium">{entry.label}</span>
                    <span className="text-muted mt-0.5 block font-mono text-[11px]">{entry.extensions}</span>
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap">
                    <Badge tone={LEVEL_TONE[entry.support]}>{LEVEL_LABEL[entry.support]}</Badge>
                  </td>
                  <td className="text-muted px-4 py-3 text-[13px] leading-relaxed">
                    {entry.summary}
                    {entry.limitation ? (
                      <span className="mt-1.5 block text-[12px] italic">{entry.limitation}</span>
                    ) : null}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>
    </section>
  );
}
